import React, { useState, useCallback } from 'react';
import {
    Card,
    CardContent,
    Typography,
    Stack,
    CircularProgress,
    Alert,
    AlertTitle
} from '@mui/material';
import { apiHandler } from '../../../../utils/api/responseHandler';
import { quoteValidator } from '../../../../utils/api/validation';
import type { QuoteData } from '../../../../types/quoter/types';
import config from '../../../../config';
import logger from '../../../../utils/logging/logger';
import { useComponentVersion } from '../../../../hooks/quoter/useComponentVersion';
import PricingDisplay from '../../../shared/PricingDisplay';

const componentName = 'SailPrice';

interface SailPriceProps {
    quoteId: number;
    viewAsCustomer?: boolean;
    variant?: 'full' | 'compact' | 'print' | 'simple';
}

interface SailPriceState {
    quoteData: QuoteData | null;
    isLoading: boolean;
    error: string | null;
}

const SailPrice: React.FC<SailPriceProps> = ({ quoteId, viewAsCustomer = false, variant = 'full' }) => {
    const [state, setState] = useState<SailPriceState>({
        quoteData: null,
        isLoading: false,
        error: null
    });

    const { masterVersion } = useComponentVersion('clothDefault', {
        onVersionChange: async (newVersion) => {
            logger.log('LogComponentUpdates', componentName, `Updating masterVersion from ${masterVersion} to `, newVersion);
            await fetchQuoteData();
        }
    });

    // Fetch quote data from API
    const fetchQuoteData = useCallback(async () => {
        if (!quoteId) return;

        const controller = new AbortController();

        try {
            logger.log('LogComponentUpdates', componentName, `fetchQuoteData: initialized`);

            setState(prev => ({
                ...prev,
                isLoading: true,
                error: null
            }));

            const response = await apiHandler.fetch<QuoteData>(
                `${config.API_URL}/api/quotes/${quoteId}`,
                {
                    validateFn: (data) => quoteValidator.validate(data),
                    signal: controller.signal
                }
            );

            if (response.error) throw response.error;

            if (response.data) {
                setState(prev => ({
                    ...prev,
                    quoteData: response.data || null,
                    isLoading: false,
                    error: null
                }));
            } else {
                logger.log('LogIssue', componentName, `!!!!!fetchQuoteData() Failed to load quote data!!!!!`);
            }
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to fetch quote data';
            console.error('SailPrice: Error fetching data:', errorMessage);

            setState(prev => ({
                ...prev,
                isLoading: false,
                error: errorMessage
            }));
        }

        return () => {
            controller.abort();
        };
    }, [quoteId]);

    if (state.isLoading) {
        return (
            <Card>
                <CardContent>
                    <Stack alignItems="center" spacing={2}>
                        <CircularProgress />
                        <Typography>Loading pricing information...</Typography>
                    </Stack>
                </CardContent>
            </Card>
        );
    }

    if (state.error) {
        return (
            <Card>
                <CardContent>
                    <Alert severity="error">
                        <AlertTitle>Error</AlertTitle>
                        {state.error}
                    </Alert>
                </CardContent>
            </Card>
        );
    }

    if (!state.quoteData) {
        return (
            <Card>
                <CardContent>
                    <Alert severity="warning">
                        <AlertTitle>No Pricing Data Available</AlertTitle>
                        Unable to load pricing information. Please check your selections.
                    </Alert>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card
            sx={{
                border: 0,
                borderColor: 'divider',
                borderRadius: 2,
                boxShadow: 'none'
            }}
        >
            <CardContent
                sx={{
                    p: 0,
                    m: 0,
                    minHeight: 'auto',
                }}
            >
                <PricingDisplay
                    quoteData={state.quoteData}
                    variant={variant}
                    showDisclaimer={false}
                    viewAsCustomer={viewAsCustomer}
                />
            </CardContent>
        </Card>
    );
};

export default SailPrice;